import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionTitle from "@/components/base/SectionTitle";
import { siteName } from "@/mocks/mangaData";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "网站定位",
    desc: "漫画e站是面向广大漫画爱好者的同好交流聚集地，汇总评论区里的读者吐槽与热门话题，并整理评分排行和推荐书单，让每一次追番都有人陪你聊。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "发展理念",
    desc: "我们相信「喜欢的事，要有人一起聊」。以真实口碑为依归，以同好共鸣为核心，不追热度、不迎合流量，只做对漫友真正有价值的内容。",
  },
  {
    icon: "ri-file-shield-2-line",
    title: "内容原则",
    desc: "坚持真实表达、理性交流与多方求证。话题信息以作品动态和读者共创为基础交叉核对，涉及传言会特别标注，评分排行配有反刷分机制。",
  },
  {
    icon: "ri-community-line",
    title: "同好文化",
    desc: "无论是骨灰级书友还是刚入坑的新人，都能在这里找到归属。友善发言、彼此尊重，是我们共同守护的交流氛围。",
  },
];

const testimonials = [
  {
    name: "追番人·小鹿",
    role: "入坑两年",
    text: "冷门神作那个书单我太爱了，把我私藏的都收录了，还挖到好几部没看过的，一口气补完。",
  },
  {
    name: "夜猫子阿橘",
    role: "每周蹲更新",
    text: "评论区简直就是我的嘴替，被刀的时候点进来看看大家破防，瞬间觉得不是只有我一个人。",
  },
  {
    name: "老书虫章鱼",
    role: "骨灰级漫友",
    text: "评分排行算是比较真实的，和我自己追的口碑基本对得上，避雷那期也帮我少踩了两个坑。",
  },
];

function ContactForm() {
  const [state, setState] = useState<"idle" | "loading" | "success">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get("company_alt") as string) ?? "";
    if (honeypot.trim()) {
      setState("success");
      form.reset();
      return;
    }
    fd.delete("company_alt");

    setState("loading");
    setFormError("");
    try {
      const res = await fetch(
        "https://readdy.ai/api/form/da2a5alv7gurs51efa6g",
        { method: "POST", body: fd }
      );
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        /* keep raw */
      }
      const message =
        parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isOk = res.ok && parsed?.code === "OK";
      if (isOk) {
        setState("success");
        form.reset();
      } else {
        setFormError(message || "提交失败，请稍后重试。");
        setState("idle");
      }
    } catch {
      setFormError("网络异常，请稍后重试。");
      setState("idle");
    }
  }

  return (
    <div className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-7">
      <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h3>
      <p className="mt-2 text-sm text-foreground-600">
        对栏目有建议、想推荐作品或洽谈合作，都可以通过表单告诉我们，我们会认真阅读每一条留言。
      </p>

      {state === "success" ? (
        <div className="mt-6 rounded-xl bg-primary-50 p-5 text-center">
          <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
            <i className="ri-check-line text-xl"></i>
          </span>
          <p className="mt-3 text-sm font-medium text-foreground-950">感谢你的留言！</p>
          <p className="mt-1 text-sm text-foreground-600">我们已收到你的反馈，会尽快与你取得联系。</p>
        </div>
      ) : (
        <form data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground-800">
                你的昵称
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="怎么称呼你"
                className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-800">
                联系邮箱
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="name@example.com"
                className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </div>
          </div>
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-foreground-800">
              留言主题
            </label>
            <select
              id="subject"
              name="subject"
              className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            >
              <option>栏目建议</option>
              <option>作品推荐</option>
              <option>合作洽谈</option>
              <option>其他</option>
            </select>
          </div>
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-foreground-800">
              留言内容
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={4}
              maxLength={500}
              placeholder="想说的话…"
              className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            ></textarea>
            <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
          </div>
          {/* 防垃圾蜜罐字段 */}
          <input
            type="text"
            name="company_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
          {formError && (
            <p className="rounded-md bg-accent-50 px-3 py-2 text-sm text-accent-800">{formError}</p>
          )}
          <button
            type="submit"
            disabled={state === "loading"}
            className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {state === "loading" ? "提交中…" : "提交留言"}
          </button>
        </form>
      )}
    </div>
  );
}

export default function About() {
  return (
    <>
      <Navbar />
      <main className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        <section className="mt-6 grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">
              <i className="ri-sparkling-2-line text-xs"></i>
              关于我们
            </span>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
              让喜欢的故事，有人一起聊
            </h1>
            <p className="mt-5 text-sm leading-8 text-foreground-700 md:text-base">
              {siteName}是一个专注于漫画爱好者交流的内容社区。我们汇总评论区里的读者吐槽与热门话题，
              整理评分排行与推荐书单，帮助每一位漫友快速找到同好交流的聚集地——无论你想追番、补番，
              还是想知道大家都在避雷什么，这里都有答案。
            </p>
            <p className="mt-3 text-sm leading-8 text-foreground-700 md:text-base">
              从「读者吐槽」到「评分排行」，从「推荐书单」到「热门话题」，我们把散落在评论区里的真实声音
              一一收集起来，让它们被更多同好看见。
            </p>
          </div>
          <div className="h-72 w-full overflow-hidden rounded-2xl lg:h-96">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20illustration%20of%20a%20cozy%20manga%20reading%20community%20with%20people%20sharing%20books%20and%20chatting%2C%20warm%20amber%20cream%20and%20muted%20red%20tones%2C%20ink%20and%20paper%20texture%2C%20friendly%20storybook%20mood%2C%20high%20detail%2C%20no%20text&width=1000&height=800&seq=about-manga&orientation=portrait"
              alt="漫画e站同好交流的温馨插画"
              className="h-full w-full object-cover"
            />
          </div>
        </section>

        <section className="mt-16">
          <SectionTitle
            align="center"
            eyebrow="我们的理念"
            title="四件事，构成了漫画e站"
            desc="从定位到文化，每一件事都围绕「让同好更好交流」而展开。"
          />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {values.map((v) => (
              <article key={v.title} className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${v.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-7 text-foreground-600">{v.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-16 w-full bg-background-100 px-4 py-14 md:px-6 md:py-16 -mx-4 md:-mx-6">
          <div className="mx-auto max-w-7xl">
            <SectionTitle
              align="center"
              eyebrow="用户口碑"
              title="同好们怎么说"
              desc="来自不同入坑时间、不同口味的漫友真实反馈。"
            />
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
              {testimonials.map((t) => (
                <figure key={t.name} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <div className="flex items-center gap-1 text-secondary-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm leading-7 text-foreground-700">“{t.text}”</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-200 font-heading text-sm font-bold text-secondary-900">
                      {t.name[0]}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <ContactForm />
          <div className="flex flex-col gap-4">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-mail-send-line text-primary-600"></i>
                商务与合作
              </h3>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                作品推广、联合书单、内容共创等合作，欢迎来信沟通。我们优先与真诚对待读者的团队合作。
              </p>
              <a
                href="mailto:hello@manhuaezhan.example"
                className="mt-3 inline-block text-sm font-medium text-primary-700 hover:text-primary-800"
              >
                hello@manhuaezhan.example
              </a>
            </div>
            <div className="rounded-2xl border border-background-200 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-chat-smile-2-line text-primary-600"></i>
                加入同好交流
              </h3>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                想第一时间参与热门话题讨论？欢迎前往话题与评论区，留下你的观点与安利。
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}